import React, { useState, useEffect, useRef } from 'react';
import { Clock, X } from 'lucide-react';
import { FieldLabel } from './TextField';

const HOURS = Array.from({ length: 24 }, (_, i) => i);          // 0–23
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

// "HH:MM" → { hour, minute }
const parse = (val) => {
  if (!val) return { hour: 0, minute: 0 };
  const [hStr, mStr] = val.split(':');
  return { hour: parseInt(hStr, 10) || 0, minute: parseInt(mStr, 10) || 0 };
};

const format = (hour, minute) =>
  `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

// "HH:MM" → "H:MM" display, e.g. "2:30"
export const formatTimeDisplay = (val) => {
  if (!val) return '--:--';
  const { hour, minute } = parse(val);
  return `${hour}:${String(minute).padStart(2, '0')}`;
};

// Shared elapsed-time picker — used in public form, task card, and condition builder.
// value: "HH:MM" string or "". onChange: (newValue: string) => void.
// compact: smaller trigger suited for inline / task-panel use.
export function TimePicker({ value, onChange, hasError, compact }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { hour, minute } = parse(value);
  const hasValue = !!value;

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const update = (h, m) => onChange(format(h, m));

  const btnBase = compact
    ? `flex items-center gap-1.5 px-2 py-1 rounded-lg border text-xs font-medium transition-all outline-none cursor-pointer bg-white hover:border-indigo-400 ${hasError ? 'border-red-400' : 'border-slate-200'} ${hasValue ? 'text-slate-800' : 'text-slate-300'}`
    : `w-full flex items-center gap-2 px-3 py-2 bg-slate-50 border rounded-lg text-sm font-medium transition-all outline-none cursor-pointer ${hasError ? 'border-red-400 ring-1 ring-red-400/20' : 'border-slate-200 hover:border-indigo-400'} ${hasValue ? 'text-slate-900' : 'text-slate-300'}`;

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} className={btnBase}>
        <Clock size={compact ? 11 : 14} className="text-slate-400 shrink-0" />
        <span className="flex-1 text-left">{formatTimeDisplay(value)}</span>
        {hasValue && (
          <span
            role="button"
            tabIndex={-1}
            onClick={(e) => { e.stopPropagation(); onChange(''); }}
            className="ml-auto text-slate-300 hover:text-slate-500 transition-colors flex items-center"
          >
            <X size={compact ? 10 : 12} />
          </span>
        )}
      </button>

      {open && (
        <div className={`absolute z-50 left-0 bg-white border border-slate-200 rounded-xl shadow-xl p-2 flex gap-1 ${compact ? 'bottom-full mb-1' : 'mt-1 top-full'}`}>
          {/* Hour column */}
          <div className="flex flex-col gap-0.5 max-h-44 overflow-y-auto">
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest px-2 pb-1">hr</p>
            {HOURS.map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => update(h, minute)}
                className={`w-9 py-1 rounded-lg text-xs font-bold transition-colors ${hour === h ? 'bg-[#4F46E5] text-white' : 'text-slate-600 hover:bg-[#F3F0FF]'}`}
              >
                {h}
              </button>
            ))}
          </div>

          <div className="flex items-center px-0.5 text-slate-300 text-xs font-bold pt-5">:</div>

          {/* Minute column */}
          <div className="flex flex-col gap-0.5 max-h-44 overflow-y-auto">
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest px-2 pb-1">min</p>
            {MINUTES.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => update(hour, m)}
                className={`w-9 py-1 rounded-lg text-xs font-bold transition-colors ${minute === m ? 'bg-[#4F46E5] text-white' : 'text-slate-600 hover:bg-[#F3F0FF]'}`}
              >
                {String(m).padStart(2, '0')}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Public-form submitter-facing field wrapper.
const TimeField = ({ field, value, onChange, status }) => (
  <div className="space-y-1.5">
    <FieldLabel field={field} />
    {field.description && (
      <p className="text-[10px] text-slate-400 font-medium leading-relaxed">{field.description}</p>
    )}
    <TimePicker
      value={value || ''}
      onChange={(v) => onChange(v)}
      hasError={status === 'error'}
    />
  </div>
);

export default TimeField;
